// React
import { useState, useCallback } from 'react';

// Components
import { Button } from '@archieai/ui';
import { ShareIcon, LinkedInIcon, XTwitterIcon } from '../../assets/icons';

// Utils
import { cn } from '../../utils/cn';

// Types
type ShareButtonsProps = {
  /** URL to share (defaults to current page) */
  url?: string;
  /** Title to share */
  title?: string;
  /** Additional className for custom styling */
  className?: string;
};

/**
 * ShareButtons - Social sharing buttons for articles
 * Includes copy link, LinkedIn, and X (Twitter)
 * Uses Button tertiary variant from @archieai/ui
 */
export const ShareButtons = ({
  url,
  title = '',
  className,
}: ShareButtonsProps) => {
  const [copied, setCopied] = useState(false);

  // Use absolute URL for copy and social sharing (relative paths break LinkedIn/copy)
  const shareUrl =
    typeof window !== 'undefined'
      ? url?.startsWith('/')
        ? window.location.origin + url
        : url || window.location.href
      : url || '';

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API support
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  const handleLinkedInShare = () => {
    const linkedInUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`;
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleXShare = () => {
    const xUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
    window.open(xUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
  };

  return (
    <div className={cn('flex items-center gap-[var(--spacing-8)]', className)}>
      {/* Copy Link */}
      <Button
        variant="tertiary"
        size="small"
        onClick={handleCopyLink}
        aria-label={copied ? 'Link copied' : 'Copy link to clipboard'}
        startIcon={copied ? <CheckIcon size={20} /> : <ShareIcon size={20} />}
      />

      {/* LinkedIn */}
      <Button
        variant="tertiary"
        size="small"
        onClick={handleLinkedInShare}
        aria-label="Share on LinkedIn"
        startIcon={<LinkedInIcon size={24} />}
      />

      {/* X (Twitter) */}
      <Button
        variant="tertiary"
        size="small"
        onClick={handleXShare}
        aria-label="Share on X"
        startIcon={<XTwitterIcon size={24} />}
      />
    </div>
  );
};

/** Simple check icon shown after successful copy */
const CheckIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
